import { useCallback, useState, type ReactNode } from "react";
import { BottomTabBar } from "@/components/layout/BottomTabBar";
import { TopBar } from "@/components/layout/TopBar";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useMobileBackButton } from "@/hooks/useMobileBackButton";
import { cn } from "@/lib/utils";

export type ViewId = "companies" | "predictions" | "news" | "calendar";

export function AppShell({
  renderSidebar,
  renderMain,
  hasSelection,
  clearSelection,
  detailTitle,
  mobileMainAsPrimary,
}: {
  renderSidebar: (view: ViewId) => ReactNode;
  renderMain: (view: ViewId) => ReactNode;
  hasSelection?: (view: ViewId) => boolean;
  clearSelection?: (view: ViewId) => void;
  detailTitle?: (view: ViewId) => string | null;
  mobileMainAsPrimary?: (view: ViewId) => boolean;
}) {
  const [view, setView] = useState<ViewId>("companies");
  const isMobile = useIsMobile();

  const mainAsPrimary = mobileMainAsPrimary?.(view) ?? false;
  const detailOpen = mainAsPrimary ? true : hasSelection ? hasSelection(view) : false;
  const showBack = isMobile && !mainAsPrimary && detailOpen;
  const onBack = useCallback(() => {
    clearSelection?.(view);
  }, [clearSelection, view]);

  useMobileBackButton(showBack, onBack);

  return (
    <div className="h-[100dvh] w-screen flex flex-col bg-background text-foreground">
      <TopBar
        view={view}
        setView={setView}
        detailOpen={showBack}
        onBack={onBack}
        detailTitle={detailTitle?.(view) ?? null}
      />
      <div className="flex-1 min-h-0 flex md:flex-row overflow-hidden relative">
        <aside
          className={cn(
            "absolute inset-0 md:static md:w-[280px] md:border-r md:border-border md:bg-card/40 md:shrink-0 overflow-hidden flex flex-col bg-card/40 md:bg-card/40 border-r-0 md:border-r",
            mainAsPrimary && "hidden md:flex",
          )}
        >
          {renderSidebar(view)}
        </aside>
        <main
          className={cn(
            "absolute inset-0 md:static md:flex-1 md:min-w-0 overflow-hidden flex flex-col bg-background",
            "transition-transform duration-200 ease-out",
            "md:translate-x-0 md:transition-none",
            detailOpen ? "translate-x-0" : "translate-x-full",
          )}
          aria-hidden={isMobile && !detailOpen ? true : undefined}
        >
          {renderMain(view)}
        </main>
      </div>
      <BottomTabBar view={view} setView={setView} className="md:hidden" />
    </div>
  );
}
